/**
 * AutoMe AI — client-side wrapper around the `askAutoMeAI` Cloud Function.
 *
 * Conversation model (RTDB):
 *
 *   users/{uid}/aiChats/
 *     activeConvId            : string         ← pointer to current conversation
 *     conversations/
 *       {convId}/
 *         title               : string         ← auto-generated from first user msg
 *         createdAt           : number (ms)
 *         updatedAt           : number (ms)    ← last activity, used for sort
 *         messages/
 *           {pushKey}         : { role, content, at }
 *
 * Each user has multiple conversations; one is "active" at any time. The
 * UI shows the active one by default and lists the others in a side panel
 * where the user can switch / delete / create.
 *
 * Legacy data under `users/{uid}/aiChats/main/messages` is auto-migrated
 * to a single conversation under the new schema on first load.
 *
 * Costs are capped server-side (20/day per user). The client just shows
 * a friendly message if the quota is hit.
 */

import { auth, ref, getFirebase } from '../../core/firebase.js';
import { LocalState } from '../../core/localState.js';
import { Firebase } from '../../app/config.js';
import { createLogger } from '../../core/logger.js';
import { getStatus as getLicenseStatus } from '../vehicle/license.service.js';
import { getUserLocation } from '../../core/geolocation.js';
import { reverseGeocode } from '../../data/apis/locationiq.js';
import { incrementUsage as incrementPhotoDiagUsage } from '../photoDiagnostic/usage.js';

const log = createLogger('ai');

/** Paths under RTDB. Kept as small functions because uid changes across logins. */
const ROOT_PATH         = (uid) => `users/${uid}/aiChats`;
const ACTIVE_CONV_PATH  = (uid) => `users/${uid}/aiChats/activeConvId`;
const CONVERSATIONS_PATH = (uid) => `users/${uid}/aiChats/conversations`;
const CONV_PATH         = (uid, id) => `users/${uid}/aiChats/conversations/${id}`;
const CONV_MESSAGES_PATH = (uid, id) => `users/${uid}/aiChats/conversations/${id}/messages`;
const LEGACY_MESSAGES_PATH = (uid) => `users/${uid}/aiChats/main/messages`;
const LEGACY_ROOT_PATH  = (uid) => `users/${uid}/aiChats/main`;

/** Trim messages within a single conversation so it doesn't grow forever. */
const MAX_PERSISTED_TURNS = 60;
/** Send only the last few turns to the model — keeps cost predictable. */
const MAX_HISTORY_SENT_TO_API = 6;
/** Auto-title length — truncated from the first user message. */
const TITLE_MAX_LEN = 40;
/** Default placeholder for a brand-new conversation (no messages yet). */
const DEFAULT_TITLE = 'שיחה חדשה';

/**
 * @typedef {Object} AiMessage
 * @property {'user'|'assistant'} role
 * @property {string} content
 * @property {number} [at]
 */

/**
 * @typedef {Object} ConversationMeta
 * @property {string} id
 * @property {string} title
 * @property {number} createdAt
 * @property {number} updatedAt
 * @property {number} messageCount
 */

// ====================================================================
// Migration — legacy `main/messages` → new conversations schema
// ====================================================================

/** Track per-uid migration so we don't re-check on every call. */
const migrated = new Set();

/**
 * If legacy messages exist under `aiChats/main/messages`, copy them into
 * a new conversation under the new schema and remove the legacy node.
 *
 * Runs at most once per uid per session. Idempotent — if no legacy data,
 * it's a single read of an empty node.
 *
 * @param {string} uid
 * @returns {Promise<string|null>}  the new conversation ID if migration ran
 */
async function migrateLegacyIfNeeded(uid) {
  if (!uid) return null;
  if (migrated.has(uid)) return null;
  try {
    const legacySnap = await ref(LEGACY_MESSAGES_PATH(uid)).once('value');
    const legacy = legacySnap.val();
    migrated.add(uid);
    if (!legacy || typeof legacy !== 'object' || Object.keys(legacy).length === 0) {
      // No legacy data — make sure the empty `main` node is gone too.
      try { await ref(LEGACY_ROOT_PATH(uid)).remove(); } catch (_) { /* ignore */ }
      return null;
    }

    // Build the new conversation: copy messages, derive title.
    const sortedKeys = Object.keys(legacy).sort();
    const messages = sortedKeys.map((k) => legacy[k]).filter(Boolean);
    const firstUser = messages.find((m) => m && m.role === 'user' && typeof m.content === 'string');
    const title = firstUser ? firstUser.content.slice(0, TITLE_MAX_LEN).trim() : 'שיחה ישנה';
    const earliest = messages.reduce(
      (min, m) => Math.min(min, Number(m && m.at) || Date.now()),
      Date.now(),
    );
    const latest = messages.reduce(
      (max, m) => Math.max(max, Number(m && m.at) || Date.now()),
      0,
    );

    const newConvRef = ref(CONVERSATIONS_PATH(uid)).push();
    const newConvId = newConvRef.key;
    if (!newConvId) return null;

    /** @type {Record<string, any>} */
    const updates = {};
    updates[`${CONVERSATIONS_PATH(uid)}/${newConvId}/title`] = title || 'שיחה ישנה';
    updates[`${CONVERSATIONS_PATH(uid)}/${newConvId}/createdAt`] = earliest;
    updates[`${CONVERSATIONS_PATH(uid)}/${newConvId}/updatedAt`] = latest || earliest;
    for (const k of sortedKeys) {
      updates[`${CONVERSATIONS_PATH(uid)}/${newConvId}/messages/${k}`] = legacy[k];
    }
    updates[`users/${uid}/aiChats/main`] = null;
    updates[`users/${uid}/aiChats/activeConvId`] = newConvId;

    await ref('').update(updates);
    log.info('migrated legacy chat → conversation', newConvId);
    return newConvId;
  } catch (err) {
    log.warn('migrate failed:', err?.message);
    return null;
  }
}

// ====================================================================
// Conversations API
// ====================================================================

/**
 * List all conversations for the current user, newest activity first.
 *
 * Empty conversations (`messageCount === 0`) are filtered out AND scheduled
 * for background deletion. This handles legacy data created before the
 * lazy-create fix; new conversations never reach RTDB until the first
 * message is sent, so empties should be rare going forward.
 *
 * @returns {Promise<ConversationMeta[]>}
 */
export async function listConversations() {
  const uid = auth().currentUser?.uid;
  if (!uid) return [];
  await migrateLegacyIfNeeded(uid);
  try {
    const snap = await ref(CONVERSATIONS_PATH(uid)).once('value');
    const obj = snap.val() || {};
    /** @type {ConversationMeta[]} */
    const arr = Object.entries(obj).map(([id, c]) => {
      const conv = /** @type {any} */ (c) || {};
      const msgs = conv.messages || {};
      return {
        id,
        title: String(conv.title || DEFAULT_TITLE),
        createdAt: Number(conv.createdAt) || 0,
        updatedAt: Number(conv.updatedAt) || Number(conv.createdAt) || 0,
        messageCount: typeof msgs === 'object' ? Object.keys(msgs).length : 0,
      };
    });

    const empties = arr.filter((c) => c.messageCount === 0);
    const nonEmpty = arr.filter((c) => c.messageCount > 0);

    // Background cleanup — don't await. Failures here don't matter; the
    // empty conv just lingers in RTDB until next time.
    if (empties.length > 0) {
      void Promise.allSettled(
        empties.map((c) => ref(CONV_PATH(uid, c.id)).remove()),
      );
    }

    nonEmpty.sort((a, b) => b.updatedAt - a.updatedAt);
    return nonEmpty;
  } catch (err) {
    log.warn('listConversations:', err?.message);
    return [];
  }
}

/**
 * Get the currently-active conversation ID, creating one if none exists.
 * @returns {Promise<string|null>}
 */
export async function getActiveConvId() {
  const uid = auth().currentUser?.uid;
  if (!uid) return null;
  await migrateLegacyIfNeeded(uid);
  try {
    const snap = await ref(ACTIVE_CONV_PATH(uid)).once('value');
    const stored = snap.val();
    if (stored && typeof stored === 'string') {
      // Verify it still exists.
      const existsSnap = await ref(CONV_PATH(uid, stored)).once('value');
      if (existsSnap.exists()) return stored;
    }
    // Fall back to the most-recent conversation.
    const list = await listConversations();
    if (list.length > 0) {
      const id = list[0].id;
      try { await ref(ACTIVE_CONV_PATH(uid)).set(id); } catch (_) { /* ignore */ }
      return id;
    }
    return null;
  } catch (err) {
    log.warn('getActiveConvId:', err?.message);
    return null;
  }
}

/**
 * Set the active conversation pointer.
 * @param {string} id
 */
export async function setActiveConvId(id) {
  const uid = auth().currentUser?.uid;
  if (!uid || !id) return;
  try { await ref(ACTIVE_CONV_PATH(uid)).set(id); }
  catch (err) { log.warn('setActiveConvId:', err?.message); }
}

/**
 * Create a new empty conversation and set it as active.
 * @returns {Promise<string|null>}  new conversation ID, or null on failure
 */
export async function createConversation() {
  const uid = auth().currentUser?.uid;
  if (!uid) return null;
  try {
    const newRef = ref(CONVERSATIONS_PATH(uid)).push();
    const id = newRef.key;
    if (!id) return null;
    const now = Date.now();
    await newRef.set({
      title: DEFAULT_TITLE,
      createdAt: now,
      updatedAt: now,
    });
    await ref(ACTIVE_CONV_PATH(uid)).set(id);
    return id;
  } catch (err) {
    log.warn('createConversation:', err?.message);
    return null;
  }
}

/**
 * Delete a conversation (and all its messages). If it was the active one,
 * picks the next-most-recent as active, or null if none left.
 * @param {string} id
 */
export async function deleteConversation(id) {
  const uid = auth().currentUser?.uid;
  if (!uid || !id) return;
  try {
    await ref(CONV_PATH(uid, id)).remove();
    const activeSnap = await ref(ACTIVE_CONV_PATH(uid)).once('value');
    if (activeSnap.val() === id) {
      const list = await listConversations();
      const next = list[0]?.id || null;
      await ref(ACTIVE_CONV_PATH(uid)).set(next);
    }
  } catch (err) {
    log.warn('deleteConversation:', err?.message);
  }
}

/**
 * Load messages for a specific conversation (defaults to the active one).
 * @param {string} [convId]
 * @returns {Promise<AiMessage[]>}
 */
export async function loadHistory(convId) {
  const uid = auth().currentUser?.uid;
  if (!uid) return [];
  await migrateLegacyIfNeeded(uid);
  const id = convId || (await getActiveConvId());
  if (!id) return [];
  try {
    const snap = await ref(CONV_MESSAGES_PATH(uid, id)).once('value');
    const obj = snap.val() || {};
    return Object.keys(obj)
      .sort()
      .map((k) => obj[k])
      .filter((m) => m && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string');
  } catch (err) {
    log.warn('loadHistory:', err?.message);
    return [];
  }
}

/**
 * Clear messages within a conversation, but keep the conversation itself
 * (its title + meta remain). Use `deleteConversation()` to remove entirely.
 * @param {string} [convId]
 */
export async function clearHistory(convId) {
  const uid = auth().currentUser?.uid;
  if (!uid) return;
  const id = convId || (await getActiveConvId());
  if (!id) return;
  try {
    await ref(CONV_MESSAGES_PATH(uid, id)).remove();
    await ref(CONV_PATH(uid, id)).update({
      title: DEFAULT_TITLE,
      updatedAt: Date.now(),
    });
  } catch (err) { log.warn('clearHistory:', err?.message); }
}

// ====================================================================
// Ask — call the Cloud Function and persist the turn
// ====================================================================

/**
 * Build the vehicle-context object the server injects into the system
 * prompt. Reads from RTDB cache only — no extra CKAN calls.
 *
 * @returns {Promise<any>}
 */
async function buildVehicleContext() {
  const plate = String(LocalState.plate || '').replace(/\D/g, '');
  if (!plate) return null;
  try {
    const govSnap = await ref(`vehicles/${plate}/gov/vehicle`).once('value');
    const gov = govSnap.val() || {};
    /** @type {any} */ let license = {};
    try { license = await getLicenseStatus(plate); } catch (_) { /* ignore */ }

    // Pass everything we have — the server-side prompt only renders the
    // fields that are actually present, so adding extra fields here is
    // safe. More context = better answers (e.g. asking "is my car fuel-
    // efficient?" benefits from engine size + fuel type).
    return {
      plate,
      year: gov.shnat_yitzur || LocalState.carYear || '',
      make: gov.tozeret_nm || '',
      model: gov.kinuy_mishari || gov.degem_nm || '',
      fuel: gov.sug_delek_nm || '',
      engine: gov.tnufat_ha_manoa || gov['tnufat_ha-manoa'] || '',
      color: gov.tzeva_rechev || '',
      trimLevel: gov.ramat_gimur || '',
      ownersCount: (typeof gov.baalut === 'number') ? gov.baalut
                 : (typeof gov.baalut === 'string' && /^\d+$/.test(gov.baalut)) ? Number(gov.baalut)
                 : null,
      lastTestDate: gov.mivchan_acharon_dt || '',
      firstRegistration: gov.moed_aliya_lakvish || '',
      licenseExpiry: license.prettyDate || '',
      licenseDaysLeft: typeof license.daysLeft === 'number' ? license.daysLeft : null,
    };
  } catch (err) {
    log.warn('buildVehicleContext failed:', err?.message);
    return null;
  }
}

/** Cached GPS used by the fuel card — same key, same TTL, same shape. */
const GPS_CACHE_KEY = 'c2c_fuel_lastGps';
const GPS_CACHE_TTL_MS = 10 * 60 * 1000;   // 10 min

/**
 * Build the user's current-location context the server injects into the
 * system prompt. The AI uses this for location-based questions ("nearest
 * authorized garage", "garage in my area", etc.).
 *
 * Strategy:
 *   1. Try the shared GPS cache (`c2c_fuel_lastGps`, 10 min TTL) — instant,
 *      no permission prompt. The fuel card and weather card also populate
 *      this cache, so most users already have a fix by the time they open
 *      the chat.
 *   2. If no cache, fetch via `getUserLocation` (native Capacitor plugin
 *      → falls back to `navigator.geolocation`). Short 5s timeout so a
 *      slow/denied permission doesn't block the chat.
 *   3. Reverse-geocode the coords to a city via LocationIQ. The result is
 *      cached client-side (4-decimal key) so subsequent calls are instant.
 *
 * Returns `null` if location is unavailable — the server prompt then tells
 * the AI it doesn't have a location, and the AI can ask the user for one
 * instead of guessing "מרכז הארץ".
 *
 * @returns {Promise<{ lat: number, lng: number, city: string, shortAddress: string } | null>}
 */
async function buildUserLocation() {
  /** @type {{ lat: number, lng: number } | null} */
  let coords = null;

  // 1) Cache hit?
  try {
    const raw = localStorage.getItem(GPS_CACHE_KEY);
    if (raw) {
      const c = JSON.parse(raw);
      if (c && c.lat && c.lng && c.at && Date.now() - c.at < GPS_CACHE_TTL_MS) {
        coords = { lat: c.lat, lng: c.lng };
      }
    }
  } catch (_) { /* ignore */ }

  // 2) Cache miss → fetch a fresh fix
  if (!coords) {
    try {
      const pos = await getUserLocation({ timeout: 5000, maxAge: GPS_CACHE_TTL_MS });
      coords = { lat: pos.lat, lng: pos.lon };
      // Populate the shared cache so the fuel card / weather card don't
      // re-request right after.
      try {
        localStorage.setItem(GPS_CACHE_KEY, JSON.stringify({ ...coords, at: Date.now() }));
      } catch (_) { /* ignore quota errors */ }
    } catch (err) {
      log.warn('getUserLocation:', err?.message);
      return null;  // permission denied or device location off
    }
  }

  // 3) Reverse-geocode → city name. Failures fall through to coords-only.
  try {
    const geo = await reverseGeocode(coords.lat, coords.lng);
    return {
      lat: coords.lat,
      lng: coords.lng,
      city: geo.city || '',
      shortAddress: geo.shortAddress || '',
    };
  } catch (err) {
    log.warn('reverseGeocode:', err?.message);
    return { lat: coords.lat, lng: coords.lng, city: '', shortAddress: '' };
  }
}

/**
 * Ask AutoMe AI a question. Persists the turn into the given conversation
 * (or the active one if omitted). Updates the conversation's title (if it
 * was still the default) and `updatedAt`.
 *
 * @param {string} message
 * @param {AiMessage[]} priorHistory
 * @param {string} [convId]  conversation to write into (default: active)
 * @param {Array<{mediaType: string, data: string}>} [images]  optional
 *   inline images for Photo Diagnostic — the server forwards them to
 *   Claude Vision. Images are NOT persisted to RTDB; only the assistant's
 *   text reply is.
 * @returns {Promise<{ answer: string, quotaLeft: number, convId: string }>}
 */
export async function ask(message, priorHistory = [], convId, images) {
  const trimmed = String(message || '').trim();
  const imgList = Array.isArray(images) ? images : [];
  // Either text OR an image is required. The server will fall back to a
  // generic "מה זה?" prompt if `message` is empty but an image is present.
  if (!trimmed && imgList.length === 0) throw new Error('empty message');

  const uid = auth().currentUser?.uid;
  if (!uid) throw new Error('not authenticated');

  // Ensure we have a conversation to write into.
  /** @type {string | null | undefined} */
  let id = convId;
  if (!id) id = await getActiveConvId();
  if (!id) id = await createConversation();
  if (!id) throw new Error('failed to create conversation');

  const fb = /** @type {any} */ (getFirebase());
  const callable = fb.app().functions(Firebase.functionsRegion).httpsCallable('askAutoMeAI');

  // Fetch vehicle + location context in parallel — both are needed for the
  // server-side system prompt. Either may return null (no plate, no GPS
  // permission, etc.); the prompt builder handles missing fields gracefully.
  const [vehicleContext, userLocation] = await Promise.all([
    buildVehicleContext(),
    buildUserLocation(),
  ]);
  const history = priorHistory.slice(-MAX_HISTORY_SENT_TO_API).map((m) => ({
    role: m.role, content: m.content,
  }));

  const result = await callable({
    message: trimmed,
    history,
    vehicleContext,
    userLocation,
    images: imgList.length > 0 ? imgList : undefined,
  });
  const data = result?.data || {};
  if (!data.ok || !data.answer) {
    throw new Error('AI returned empty answer');
  }

  // Persist the new turn (user + assistant) to RTDB. When the user attached
  // images, we annotate the persisted content with a `📷` prefix so the
  // history view (and re-load) shows there was a photo even though we
  // don't store the photo itself.
  const persistedUserContent = imgList.length > 0
    ? (trimmed ? `📷 ${trimmed}` : '📷 [תמונה לאבחון]')
    : trimmed;
  void persistTurn(
    { role: 'user', content: persistedUserContent },
    { role: 'assistant', content: data.answer },
    id,
  );

  // Photo Diagnostic usage tracking — drives the bold "✨ חדש" hero card
  // on the home grid (auto-fades after 3 successful diagnoses).
  if (imgList.length > 0) {
    try { incrementPhotoDiagUsage(); } catch (_) { /* ignore */ }
  }

  return { answer: data.answer, quotaLeft: Number(data.quotaLeft || 0), convId: id };
}

/**
 * Append two turns to a conversation's history. Updates the conversation's
 * title (if still default) and `updatedAt`. Trims to MAX_PERSISTED_TURNS by
 * deleting the oldest messages after the write.
 *
 * @param {AiMessage} userTurn
 * @param {AiMessage} aiTurn
 * @param {string} convId
 */
async function persistTurn(userTurn, aiTurn, convId) {
  const uid = auth().currentUser?.uid;
  if (!uid || !convId) return;
  try {
    const now = Date.now();
    const msgsRef = ref(CONV_MESSAGES_PATH(uid, convId));
    await msgsRef.push().set({ ...userTurn, at: now });
    await msgsRef.push().set({ ...aiTurn, at: now + 1 });

    // Update conversation meta: title (if still default) + updatedAt.
    const metaRef = ref(CONV_PATH(uid, convId));
    const metaSnap = await metaRef.once('value');
    const meta = metaSnap.val() || {};
    /** @type {Record<string, any>} */
    const metaUpdates = { updatedAt: now };
    if (!meta.title || meta.title === DEFAULT_TITLE) {
      metaUpdates.title = userTurn.content.slice(0, TITLE_MAX_LEN).trim();
    }
    await metaRef.update(metaUpdates);

    // Trim oldest if over limit.
    const allSnap = await msgsRef.once('value');
    const all = allSnap.val() || {};
    const keys = Object.keys(all).sort();
    const excess = keys.length - MAX_PERSISTED_TURNS;
    if (excess > 0) {
      /** @type {Record<string, null>} */
      const trimUpdates = {};
      for (let i = 0; i < excess; i++) trimUpdates[keys[i]] = null;
      await msgsRef.update(trimUpdates);
    }
  } catch (err) { log.warn('persistTurn:', err?.message); }
}
